import { useCallback, useEffect, useMemo } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { Spacing16 } from 'autogen/design-tokens/tokens';
import type { ContentfulArticleSearchResponse, GetArticlePageParams } from 'autogen/swagger/all';
import ContainerFluid from 'components/Layout/components/ContainerFluid';
import FlexColumn from 'components/Layout/components/Flex/components/FlexColumn';
import ArticleUniverseLayout from 'components/pages/ArticleUniverse/ArticleUniverseLayout';
import CardLayoutRenderer from 'components/pages/ArticleUniverse/components/CardLayoutRenderer';
import Filter from 'components/pages/ArticleUniverse/components/Filter';
import ArticleSearchResponseContext from 'components/pages/ArticleUniverse/components/Filter/context/ArticleSearchResponseContext';
import { useUpdateArticlePage } from 'components/pages/ArticleUniverse/hooks/updateArticlePage';
import { useIsPreview } from 'components/pages/Contentful/context/PreviewContext';
import { filterPublishedFields } from 'components/pages/Contentful/shared/ContentfulHelpers';
import isTruthy from 'helpers/isTruthy';
import useDefaultDataLayerTrackingEvent from 'modules/tracking/GA4/hooks/useSetupDefaultDataLayerTrackingEvent';
import type { TypeSectionTemplateDetailed } from 'types/autogen/contentful';
import TemplateComponentRenderer from '../../shared/TemplateComponentRenderer';

function getSectionQueryParameterNames(
    sections: ContentfulArticleSearchResponse['sections'],
    currentSectionId: ContentfulArticleSearchResponse['currentSectionId']
): GetArticlePageParams['section'] {
    if (currentSectionId) {
        const queryNameFromCurrentSectionId = sections.find(
            (section) => section.entryId === currentSectionId
        )?.queryParameterName;
        return queryNameFromCurrentSectionId ? [queryNameFromCurrentSectionId] : undefined;
    }
    return sections?.map((section) => section.queryParameterName)?.filter(isTruthy); // .filter(Boolean) doesn't work with TS
}

function SectionTemplate(props: TypeSectionTemplateDetailed) {
    const { fields } = props;
    const { contentAbove, contentBelow, articleSearchResponse, section, headline } = fields;
    const { data, updateArticlePage } = useUpdateArticlePage(articleSearchResponse);
    const { sections = [], topics, currentSectionId, frontPageUrl } = data || articleSearchResponse || {};
    const currentSection = useMemo(() => getSectionQueryParameterNames(sections, currentSectionId), []);
    const defaultDataLayerTrackingEvent = useDefaultDataLayerTrackingEvent({ pageType: 'ArticleOverview' });

    const contentAboveToRender = filterPublishedFields(contentAbove);
    const contentBelowToRender = filterPublishedFields(contentBelow);

    useEffect(() => {
        defaultDataLayerTrackingEvent();
    }, [defaultDataLayerTrackingEvent]);

    const onUpdateArticlePage = useCallback(
        async (params: Partial<GetArticlePageParams>, appendArticles?: boolean) => {
            await new Promise<void>((resolve) => {
                updateArticlePage(
                    {
                        page: params.page || 1,
                        section: currentSection,
                        topic: params.topic
                    },
                    appendArticles
                );

                resolve();
            });
        },
        [updateArticlePage, currentSection]
    );

    return (
        <ArticleUniverseLayout
            sections={sections}
            headline={headline}
            sectionName={section?.fields.publicName}
            frontPageUrl={frontPageUrl}
            currentSectionId={currentSectionId}
        >
            {contentAboveToRender?.map((entity) => (
                <TemplateComponentRenderer key={entity.sys.id} {...entity} />
            ))}

            <ArticleSearchResponseContext.Provider value={{ data, updateArticlePage: onUpdateArticlePage }}>
                <FlexColumn gap={Spacing16}>
                    <ContainerFluid>
                        <Filter topics={topics || []} sections={[]} />
                    </ContainerFluid>

                    {data && <CardLayoutRenderer articleGroups={data.articleGroups} />}
                </FlexColumn>
            </ArticleSearchResponseContext.Provider>

            {contentBelowToRender?.map((entity) => (
                <TemplateComponentRenderer key={entity.sys.id} {...entity} />
            ))}
        </ArticleUniverseLayout>
    );
}

export default withErrorBoundary(SectionTemplate, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
